import React, { useEffect, useState } from "react";
import { IDoctor, ITreatment, ISubTreatment } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export interface CatalogTreatment {
  id: string;
  name: string;
  description: string;
}

interface Props {
  catalog: CatalogTreatment;
  /** The clinic's configured bookTreatment row for this catalog entry, if any. */
  treatment: ITreatment | null;
  /** Bookable dentists (IsBookDentist) shown as assignment checkboxes. */
  dentists: IDoctor[];
  isDentistAssigned: (doctor: IDoctor, treatment: ITreatment) => boolean;
  isSavingPrices: boolean;
  onSavePrices: (localPrice: string, foreginPrice: string) => void;
  onToggleDentist: (doctor: IDoctor) => void;
  onDelete: () => void;
  onAddSub: () => void;
  onEditSub: (sub: ISubTreatment) => void;
  onDeleteSub: (subId: string) => void;
}

function formatPrice(value: string | number | undefined, currency: string) {
  if (value === "" || value === undefined || value === null) return "—";
  return `${value} ${currency}`;
}

function displayNote(note: string | number | undefined) {
  if (note === "" || note === undefined || note === null || note === 0) return "—";
  return String(note);
}

const thClass =
  "px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500 whitespace-nowrap bg-slate-50/80";
const tdClass = "px-3 py-2.5 text-xs text-slate-700 align-top border-t border-slate-100";

const priceInputClass =
  "w-[110px] rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-800 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30";

export const TreatmentCard: React.FC<Props> = ({
  catalog,
  treatment,
  dentists,
  isDentistAssigned,
  isSavingPrices,
  onSavePrices,
  onToggleDentist,
  onDelete,
  onAddSub,
  onEditSub,
  onDeleteSub,
}) => {
  const [subDeleteCandidate, setSubDeleteCandidate] = useState<string | null>(null);
  const [localPrice, setLocalPrice] = useState<string>(
    treatment?.localPrice != null ? String(treatment.localPrice) : ""
  );
  const [foreginPrice, setForeginPrice] = useState<string>(
    treatment?.foreginPrice != null ? String(treatment.foreginPrice) : ""
  );

  useEffect(() => {
    setLocalPrice(treatment?.localPrice != null ? String(treatment.localPrice) : "");
    setForeginPrice(treatment?.foreginPrice != null ? String(treatment.foreginPrice) : "");
  }, [treatment?.localPrice, treatment?.foreginPrice]);

  const isConfigured = !!treatment;
  const assignedCount = treatment
    ? dentists.filter((d) => isDentistAssigned(d, treatment)).length
    : 0;
  const isHiddenPublicly = isConfigured && assignedCount === 0;

  const pricesDirty =
    localPrice !== (treatment?.localPrice != null ? String(treatment.localPrice) : "") ||
    foreginPrice !== (treatment?.foreginPrice != null ? String(treatment.foreginPrice) : "");

  const handleDeleteConfirm = () => {
    if (subDeleteCandidate) {
      onDeleteSub(subDeleteCandidate);
      setSubDeleteCandidate(null);
    }
  };

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white shadow-sm ${
        isConfigured ? "border-slate-200" : "border-dashed border-slate-300"
      }`}
    >
      {/* Fixed catalog treatment: name + default description; clinic sets prices inline. */}
      <div className="flex flex-col gap-3 p-3 sm:p-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 lg:max-w-[46%]">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-bold text-slate-900 mb-0">{catalog.name}</h4>
            {isConfigured ? (
              isHiddenPublicly ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                  <VisibilityOffIcon sx={{ fontSize: 12 }} />
                  Hidden on BookMyDentist
                </span>
              ) : (
                <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                  Live · {assignedCount} dentist{assignedCount === 1 ? "" : "s"}
                </span>
              )
            ) : (
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Not offered
              </span>
            )}
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-slate-600">
            {catalog.description || "—"}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2.5">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">
              Local price (LKR)
            </label>
            <input
              type="number"
              min="0"
              value={localPrice}
              onChange={(e) => setLocalPrice(e.target.value)}
              placeholder="e.g. 1500"
              className={priceInputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">
              Foreigner price (USD)
            </label>
            <input
              type="number"
              min="0"
              value={foreginPrice}
              onChange={(e) => setForeginPrice(e.target.value)}
              placeholder="e.g. 20"
              className={priceInputClass}
            />
          </div>
          <button
            type="button"
            disabled={isSavingPrices || (isConfigured && !pricesDirty)}
            onClick={() => onSavePrices(localPrice, foreginPrice)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3.5 py-2 text-[11px] font-semibold text-white shadow-sm transition-all hover:shadow-md hover:shadow-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SaveAltIcon sx={{ fontSize: 14 }} />
            {isSavingPrices ? "Saving…" : isConfigured ? "Update prices" : "Offer treatment"}
          </button>
          {isConfigured ? (
            <button
              type="button"
              className="p-1.5 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
              onClick={onDelete}
              aria-label={`Stop offering ${catalog.name}`}
              title="Stop offering this treatment"
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Dentist assignment: unticked everywhere → the treatment is hidden on BookMyDentist. */}
      <div className="border-t border-slate-100 bg-slate-50/40 px-3 py-2.5 sm:px-4">
        <h5 className="text-[11px] font-bold uppercase tracking-wide text-slate-600 mb-0">
          Dentists providing this treatment
        </h5>
        {!isConfigured ? (
          <p className="mt-1.5 text-xs text-slate-500">Set prices first to assign dentists.</p>
        ) : dentists.length === 0 ? (
          <p className="mt-1.5 text-xs text-slate-500">
            No bookable dentists yet — add your team in the Team step.
          </p>
        ) : (
          <>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
              {dentists.map((doctor) => (
                <label
                  key={doctor.id}
                  className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-700"
                >
                  <input
                    type="checkbox"
                    checked={isDentistAssigned(doctor, treatment!)}
                    onChange={() => onToggleDentist(doctor)}
                    className="w-3.5 h-3.5 text-primary border-slate-300 rounded focus:ring-primary"
                  />
                  Dr. {`${doctor.firstName} ${doctor.lastName}`.trim()}
                </label>
              ))}
            </div>
            {isHiddenPublicly ? (
              <p className="mt-2 text-[11px] font-medium text-amber-700">
                Tick at least one dentist — until then this treatment is not shown on
                BookMyDentist.lk.
              </p>
            ) : null}
          </>
        )}
      </div>

      {/* Sub-treatments (configured treatments only) */}
      {isConfigured ? (
        <div className="border-t border-slate-200 bg-slate-50/40">
          <div className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4">
            <h5 className="text-[11px] font-bold uppercase tracking-wide text-slate-600 mb-0">
              Sub-treatments
            </h5>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 self-start rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-all hover:shadow-md hover:shadow-primary/20"
              onClick={onAddSub}
            >
              <AddIcon sx={{ fontSize: 14 }} />
              Add sub-treatment
            </button>
          </div>

          {treatment!.subTreatments.length === 0 ? (
            <p className="px-4 pb-3 text-xs text-slate-500">No sub-treatments yet.</p>
          ) : (
            <div className="overflow-x-auto pb-1">
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Description</th>
                    <th className={thClass}>Duration (min)</th>
                    <th className={thClass}>Local price</th>
                    <th className={thClass}>Min local</th>
                    <th className={thClass}>Max local</th>
                    <th className={thClass}>Foreigner price</th>
                    <th className={thClass}>Min foreigner</th>
                    <th className={thClass}>Max foreigner</th>
                    <th className={thClass}>Note</th>
                    <th className={`${thClass} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {treatment!.subTreatments.map((sub) => (
                    <tr key={sub.id} className="bg-white hover:bg-slate-50/80 transition-colors">
                      <td className={`${tdClass} font-medium text-slate-900`}>
                        {sub.subTreatment || "—"}
                      </td>
                      <td className={`${tdClass} max-w-[180px]`}>
                        <span className="line-clamp-3">{sub.description || "—"}</span>
                      </td>
                      <td className={tdClass}>{sub.duration ?? "—"}</td>
                      <td className={tdClass}>{formatPrice(sub.localPrice, "LKR")}</td>
                      <td className={tdClass}>{formatPrice(sub.minlocalPrice, "LKR")}</td>
                      <td className={tdClass}>{formatPrice(sub.maxlocalPrice, "LKR")}</td>
                      <td className={tdClass}>{formatPrice(sub.foreignerPrice, "USD")}</td>
                      <td className={tdClass}>{formatPrice(sub.minforeignerPrice, "USD")}</td>
                      <td className={tdClass}>{formatPrice(sub.maxforeignerPrice, "USD")}</td>
                      <td className={`${tdClass} max-w-[140px]`}>
                        <span className="line-clamp-2">{displayNote(sub.note)}</span>
                      </td>
                      <td className={`${tdClass} text-right`}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            className="px-2 py-1 text-[11px] font-semibold text-slate-600 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                            onClick={() => onEditSub(sub)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="p-1.5 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                            onClick={() => setSubDeleteCandidate(sub.id)}
                            aria-label="Delete sub-treatment"
                            title="Delete"
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {subDeleteCandidate && (
        <ConfirmationModal
          title="Delete Sub-Treatment"
          message="Are you sure you want to delete this sub-treatment?"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setSubDeleteCandidate(null)}
        />
      )}
    </div>
  );
};
